import { authClient } from "@/api/auth-client";
import type { TokenSource } from "@its-engineering-and-development/rbac-react";

/**
 * Polling interval for checking token changes.
 * Set to 5 minutes (60 * 1000 * 5) to align with the 15-minute JWT lifetime.
 * This reduces unnecessary polling frequency and prevents potential
 * disruption/re-renders caused by high-frequency token change detection.
 */
const TOKEN_POLL_INTERVAL_MS = 60 * 1000 * 5;

function readAccessToken(): string | null {
    try {
        return authClient.getAccessToken() ?? null;
    } catch {
        return null;
    }
}

export class ExpenseAdminTokenSource implements TokenSource {
    private readonly listeners = new Set<(token: string | null) => void>();
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private lastToken: string | null = null;

    getAccessToken(): string | null {
        return readAccessToken();
    }

    onTokenChange(cb: (token: string | null) => void): () => void {
        this.listeners.add(cb);
        this.lastToken = readAccessToken();

        if (!this.intervalId) {
            this.intervalId = setInterval(() => {
                const nextToken = readAccessToken();

                if (nextToken !== this.lastToken) {
                    this.lastToken = nextToken;
                    this.emit(nextToken);
                }
            }, TOKEN_POLL_INTERVAL_MS);
        }

        return () => {
            this.listeners.delete(cb);
            if (this.listeners.size === 0 && this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        };
    }

    /**
     * Push-based notification: re-reads the stored token and emits immediately
     * if it changed. Call right after login/logout so subscribers (RbacProvider,
     * JwtAuthProvider) react at once instead of waiting for the next poll tick,
     * which prevents the post-login "access denied" flash.
     */
    notifyTokenChange(): void {
        const nextToken = readAccessToken();
        if (nextToken !== this.lastToken) {
            this.lastToken = nextToken;
            this.emit(nextToken);
        }
    }

    private emit(token: string | null): void {
        for (const listener of this.listeners) {
            listener(token);
        }
    }
}

export const expenseAdminTokenSource = new ExpenseAdminTokenSource();
